// src/components/organization/TeamManagement.jsx
import React, { useState } from 'react';
import { useOrganization } from '../../hooks/useOrganization';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import StatusBadge from '../common/StatusBadge';
import InviteUser from './InviteUser';
import ConfirmDialog from '../common/ConfirmDialog';
import { UserCircleIcon, EnvelopeIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { formatDate } from '../../utils/formatters';
import { useTranslation } from 'react-i18next';

const TeamManagement = () => {
    const { t } = useTranslation();
  const { user } = useAuth();
  // ✅ useOrganization usato UNA sola volta — niente istanze duplicate
  const {
    users, invitations, loading,
    removeUser, cancelInvitation, resendInvitation, inviteUser, refresh
  } = useOrganization();

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const [inviteToCancel, setInviteToCancel] = useState(null);

  const handleRemoveUser = async () => {
    if (userToRemove) {
      await removeUser(userToRemove.id);
      setUserToRemove(null);
    }
  };

  const handleCancelInvitation = async () => {
    if (inviteToCancel) {
      await cancelInvitation(inviteToCancel.id);
      setInviteToCancel(null);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{t('team.title')}</h2>
          <p className="text-sm text-gray-500 mt-1">Gestisci i membri del tuo team</p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          + Invita membro
        </button>
      </div>

      {/* Membri */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-medium text-gray-900">Membri del team ({users?.length || 0})</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {users?.length === 0 && (
            <p className="px-6 py-4 text-sm text-gray-500">Nessun membro trovato</p>
          )}
          {users?.map((member) => (
            <div key={member.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <UserCircleIcon className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    {member.firstName} {member.lastName}
                    {member.id === user?.id && (
                      <span className="ml-2 text-xs text-gray-500">(Tu)</span>
                    )}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <EnvelopeIcon className="w-3 h-3 text-gray-400" />
                    <span className="text-sm text-gray-500">{member.email}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={member.role} size="sm" />
                {member.id !== user?.id && (
                  <button
                    onClick={() => setUserToRemove(member)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Rimuovi
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Inviti in sospeso */}
      {invitations?.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="font-medium text-gray-900">Inviti in sospeso ({invitations.length})</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {invitations.map((invite) => (
              <div key={invite.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                    <EnvelopeIcon className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{invite.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <CalendarIcon className="w-3 h-3 text-gray-400" />
                      <span className="text-sm text-gray-500">
                        Inviato il {formatDate(invite.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={invite.role} size="sm" />
                  <button
                    onClick={() => resendInvitation(invite.id)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Rinvia
                  </button>
                  <button
                    onClick={() => setInviteToCancel(invite)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Annulla
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ✅ onInvite passato come prop — InviteUser non usa più useOrganization */}
      <InviteUser
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={refresh}
        onInvite={inviteUser}
      />

      {userToRemove && (
        <ConfirmDialog
          title="Rimuovi membro"
          message={`Sei sicuro di voler rimuovere ${userToRemove.firstName} ${userToRemove.lastName}?`}
          confirmText="Rimuovi"
          type="danger"
          onConfirm={handleRemoveUser}
          onCancel={() => setUserToRemove(null)}
        />
      )}

      {inviteToCancel && (
        <ConfirmDialog
          title="Annulla invito"
          message={`Annullare l'invito per ${inviteToCancel.email}?`}
          confirmText="Annulla invito"
          type="warning"
          onConfirm={handleCancelInvitation}
          onCancel={() => setInviteToCancel(null)}
        />
      )}
    </div>
  );
};

export default TeamManagement;